import { Masonry } from "@mui/lab";
import { Box, MenuItem, TextField, useTheme } from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { ProjetosCamadasFormularios, Tabelas, ConfigCaracterizacao } from "../../../interfaces";
import { DashboardContext } from "../../../providers/Dashboard";
import { DebounceProvider } from "../../../providers/Debounce";
import { getStructure } from "../../../services/db";
import { getFormPreference, saveFormPreference } from "../../../services/utils";
import { getMunicipio } from "../../../services/data";
import Input from "../Input";

export default function Form(props: { formularios: ProjetosCamadasFormularios[]; tabela: Tabelas; configCaracterizacao?: ConfigCaracterizacao; }) {
  const { metadata } = useContext(DashboardContext);
  const theme = useTheme();

  const [currentFormIndex, setCurrentFormIndex] = useState(0);

  const masonryColumns = metadata.drawer
    ? { xs: 1, sm: 1, md: 3, lg: 4, xl: 6 }
    : { xs: 1, sm: 2, md: 3, lg: 6, xl: 8 };

  function handleFormChange(e: React.ChangeEvent<HTMLInputElement>) {
    setCurrentFormIndex(Number(e.target.value));
    saveFormPreference(props.tabela, e.target.value);
  }

  useEffect(() => {
    const form = getFormPreference(props.tabela);
    if (form) {
      setCurrentFormIndex(Number(form));
    }
  }, [props.tabela]);

  const currentForm = props.formularios[currentFormIndex] || null;

  const camposNormalizados = useMemo(() => {
    if (!currentForm?.projetosCamadasFormulariosCampos) return [];

    const structure = getStructure(props.tabela) || {};

    return currentForm.projetosCamadasFormulariosCampos
      .filter((campo) => campo.ativo)
      .sort((a, b) => a.ordem - b.ordem)
      .map((campo) => {
        const tblNomeColuna = campo.tblNomeColuna?.trim?.();

        if (!tblNomeColuna) {
          return campo;
        }

        campo.tblNomeColuna = tblNomeColuna;

        if (!Object.prototype.hasOwnProperty.call(structure, tblNomeColuna)) {
          console.warn(
            `O campo ${tblNomeColuna} não existe na structure da tabela ${props.tabela}. Ele será renderizado e adicionado localmente.`,
          );

          structure[tblNomeColuna] = metadata?.[tblNomeColuna] ?? null;
        }

        return campo;
      });
  }, [currentForm, props.tabela, metadata]);

  useEffect(() => {
    if (!currentForm?.projetosCamadasFormulariosCampos) return;

    const storageKey = `structure@${props.tabela}`;
    const currentStructure = getStructure(props.tabela) || {};
    let hasChanges = false;

    const updatedStructure = { ...currentStructure };

    currentForm.projetosCamadasFormulariosCampos
      .filter((campo) => campo.ativo && campo.tblNomeColuna)
      .forEach((campo) => {
        const nomeColuna = campo.tblNomeColuna as string;

        if (!Object.prototype.hasOwnProperty.call(updatedStructure, nomeColuna)) {
          updatedStructure[nomeColuna] = metadata?.[nomeColuna] ?? null;
          hasChanges = true;
        }
      });

    if (hasChanges) {
      localStorage.setItem(storageKey, JSON.stringify(updatedStructure));
      console.info(`Structure da tabela ${props.tabela} atualizada com campos novos do formulário.`);
    }
  }, [currentForm, props.tabela, metadata]);

  return (
    <>
      <Box
        sx={{
          paddingTop: "1rem",
        }}
      >
        <TextField
          fullWidth
          select
          label="Selecione um formulário"
          value={props.formularios.length > 0 ? currentFormIndex : ""}
          defaultValue={0}
          onChange={handleFormChange}
          InputLabelProps={{
            sx: {
              "&.Mui-focused": {
                color: theme.palette.secondary.main,
              },
            },
          }}
        >
          {props.formularios.length > 0 ? (
            props.formularios.map((formulario, index) => (
              <MenuItem key={formulario.nome} value={index}>
                {formulario.nome}
              </MenuItem>
            ))
          ) : (
            <MenuItem value="" disabled>
              Nenhum formulário encontrado
            </MenuItem>
          )}
        </TextField>
      </Box>

      <Box
        sx={{
          paddingTop: "1rem",
        }}
      >
        <Masonry columns={masonryColumns} spacing={1}>
          {camposNormalizados.map((campo, index) => {
            if (
              campo.tblNomeColuna === "id_bairro" ||
              campo.tblNomeColuna === "id_logradouro" ||
              campo.tblNomeColuna === "id_logradouro_tributario"
            ) {
              campo.tipo = 8;
              campo.tipoCampo = "8";
            }
            
            if (
              props.configCaracterizacao?.filtrarSecaoPorLogradouro &&
              props.tabela === "imobiliario" &&
              (campo.tblNomeColuna === "id_secao" ||
                campo.tblNomeColuna === "id_segmento_logradouro_tributario")
            ) {
              campo.tipo = 8;
              campo.tipoCampo = "8";
          
              const campoLogradouro =
                campo.tblNomeColuna === "id_segmento_logradouro_tributario"
                  ? "id_logradouro_tributario"
                  : "id_logradouro";

              campo.filtraCampo = campoLogradouro;
              campo.colunaFiltroRetorno = campoLogradouro;
            }
            
            return (
              <DebounceProvider key={index}>
                <Input
                  key={`${metadata[`id_${props.tabela}`]!}@${props.tabela}@${campo.tblNomeColuna}`}
                  tabela={props.tabela}
                  campo={campo}
                />
              </DebounceProvider>
            );
          })}
        </Masonry>
      </Box>
    </>
  );
}
